'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  ShieldCheck,
  Users,
  Zap,
  CheckCircle2,
  ArrowRight,
  Upload,
  Search,
  FileCheck,
  Wallet,
  Trophy,
  Sparkles,
} from 'lucide-react';

interface Step {
  number: string;
  title: string;
  description: string;
  details: string[];
  illustration: React.ReactNode;
}

// Isometric-style illustrations for each step
function ApplicationIllustration({ isActive }: { isActive: boolean }) {
  return (
    <div className="relative w-full h-48 flex items-center justify-center">
      {/* Laptop base */}
      <div
        className={cn(
          'relative transition-all duration-700',
          isActive ? 'scale-100 opacity-100' : 'scale-90 opacity-60'
        )}
      >
        {/* Laptop body */}
        <div className="relative">
          <div className="w-40 h-28 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg shadow-lg transform rotate-[-5deg] skew-y-1">
            {/* Screen */}
            <div className="absolute inset-2 bg-gradient-to-br from-purple-50 to-white rounded overflow-hidden">
              {/* Form fields */}
              <div className="p-2 space-y-1.5">
                <div className="h-2 w-12 bg-purple-200 rounded" />
                <div className="h-3 w-full bg-gray-100 rounded" />
                <div className="h-2 w-10 bg-purple-200 rounded mt-2" />
                <div className="h-3 w-full bg-gray-100 rounded" />
                <div className="h-2 w-14 bg-purple-200 rounded mt-2" />
                <div className="h-3 w-3/4 bg-gray-100 rounded" />
              </div>
            </div>
          </div>
          {/* Keyboard */}
          <div className="w-44 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded-b-lg -mt-1 ml-[-2px] transform skew-y-1" />
        </div>

        {/* Floating documents */}
        <div
          className={cn(
            'absolute -top-4 -right-6 transition-all duration-500 delay-200',
            isActive ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          )}
        >
          <div className="w-10 h-12 bg-white rounded shadow-md border border-gray-100 flex items-center justify-center">
            <Upload className="w-4 h-4 text-purple-500" />
          </div>
        </div>

        {/* Verification badge appearing */}
        <div
          className={cn(
            'absolute -bottom-2 -right-4 transition-all duration-700 delay-500',
            isActive ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
          )}
        >
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-500 rounded-full shadow-lg flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MatchingIllustration({ isActive }: { isActive: boolean }) {
  return (
    <div className="relative w-full h-48 flex items-center justify-center">
      <div className="relative flex items-center justify-center">
        {/* Left profile card */}
        <div
          className={cn(
            'absolute transition-all duration-700',
            isActive
              ? 'left-[-70px] opacity-100'
              : 'left-[-40px] opacity-60'
          )}
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          <div className="w-20 h-24 bg-white rounded-xl shadow-lg border border-gray-100 p-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 mx-auto mb-2" />
            <div className="h-1.5 w-12 bg-gray-200 rounded mx-auto mb-1" />
            <div className="h-1 w-8 bg-gray-100 rounded mx-auto" />
            <div className="flex justify-center gap-1 mt-2">
              <div className="w-3 h-3 rounded bg-purple-100" />
              <div className="w-3 h-3 rounded bg-purple-100" />
            </div>
          </div>
        </div>

        {/* Right profile card */}
        <div
          className={cn(
            'absolute transition-all duration-700 delay-100',
            isActive
              ? 'right-[-70px] opacity-100'
              : 'right-[-40px] opacity-60'
          )}
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          <div className="w-20 h-24 bg-white rounded-xl shadow-lg border border-gray-100 p-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 mx-auto mb-2" />
            <div className="h-1.5 w-12 bg-gray-200 rounded mx-auto mb-1" />
            <div className="h-1 w-8 bg-gray-100 rounded mx-auto" />
            <div className="flex justify-center gap-1 mt-2">
              <div className="w-3 h-3 rounded bg-amber-100" />
              <div className="w-3 h-3 rounded bg-amber-100" />
            </div>
          </div>
        </div>

        {/* Center matching indicator */}
        <div
          className={cn(
            'relative z-10 transition-all duration-700 delay-300',
            isActive ? 'scale-100 opacity-100' : 'scale-90 opacity-70'
          )}
        >
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full shadow-xl flex items-center justify-center">
            <Users className="w-7 h-7 text-white" />
          </div>

          {/* Pulse rings */}
          {isActive && (
            <div
              className="absolute inset-0 rounded-full border-2 border-purple-400 animate-ping"
              style={{ animationDuration: '2s' }}
            />
          )}
        </div>

        {/* Match success sparkles */}
        {isActive && (
          <>
            <Sparkles
              className="absolute -top-6 left-4 w-4 h-4 text-amber-400 animate-pulse"
              style={{ animationDelay: '0.5s' }}
            />
            <Sparkles
              className="absolute -top-4 right-2 w-3 h-3 text-purple-400 animate-pulse"
              style={{ animationDelay: '0.8s' }}
            />
            <Sparkles
              className="absolute top-0 -right-8 w-4 h-4 text-violet-400 animate-pulse"
              style={{ animationDelay: '1s' }}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ExecutionIllustration({ isActive }: { isActive: boolean }) {
  return (
    <div className="relative w-full h-48 flex items-center justify-center">
      <div className="relative flex items-center gap-3">
        {/* Document with checkmark */}
        <div
          className={cn(
            'transition-all duration-500',
            isActive ? 'translate-x-0 opacity-100' : 'translate-x-0 opacity-60'
          )}
        >
          <div className="relative w-12 h-16 bg-white rounded-lg shadow-lg border border-gray-100 p-2">
            <div className="space-y-1">
              <div className="h-1 w-full bg-gray-200 rounded" />
              <div className="h-1 w-3/4 bg-gray-200 rounded" />
              <div className="h-1 w-full bg-gray-200 rounded" />
              <div className="h-1 w-1/2 bg-gray-200 rounded" />
            </div>
            <div
              className={cn(
                'absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center transition-all duration-500 delay-300',
                isActive ? 'scale-100' : 'scale-75 opacity-50'
              )}
            >
              <FileCheck className="w-2.5 h-2.5 text-white" />
            </div>
          </div>
        </div>

        {/* Arrow */}
        <ArrowRight
          className={cn(
            'w-4 h-4 text-purple-300 transition-all duration-500 delay-200',
            isActive ? 'opacity-100' : 'opacity-40'
          )}
        />

        {/* Escrow vault */}
        <div
          className={cn(
            'transition-all duration-500 delay-300',
            isActive ? 'translate-y-0 opacity-100' : 'translate-y-0 opacity-60'
          )}
        >
          <div className="relative w-14 h-12 bg-gradient-to-br from-purple-600 to-violet-700 rounded-lg shadow-lg flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" />
            {/* Lock indicator */}
            <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-400 rounded-full flex items-center justify-center">
              <div className="w-1 h-1 bg-white rounded-full" />
            </div>
          </div>
        </div>

        {/* Arrow */}
        <ArrowRight
          className={cn(
            'w-4 h-4 text-purple-300 transition-all duration-500 delay-400',
            isActive ? 'opacity-100' : 'opacity-40'
          )}
        />

        {/* Success trophy */}
        <div
          className={cn(
            'transition-all duration-700 delay-500',
            isActive ? 'scale-100 opacity-100' : 'scale-90 opacity-60'
          )}
        >
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full shadow-lg flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            {/* Celebration particles */}
            {isActive && (
              <>
                <div className="absolute -top-3 left-1/2 w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.6s' }} />
                <div className="absolute -top-1 -left-2 w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0.8s' }} />
                <div className="absolute -top-2 -right-2 w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '1s' }} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const steps: Step[] = [
  {
    number: '01',
    title: 'Apply & Get Verified',
    description:
      'Submit your application with credentials. We verify your background and match you to the right role in our ecosystem.',
    details: [
      'Role-specific verification process',
      'Background and credential check',
      'Invitation to the ecosystem',
    ],
    illustration: <ApplicationIllustration isActive={false} />,
  },
  {
    number: '02',
    title: 'Get Matched to Deals',
    description:
      'Our intelligent matching system connects you with relevant opportunities based on expertise, interest, and fit.',
    details: [
      'AI-powered matching algorithm',
      'Mutual acceptance workflow',
      'Clear scope and expectations',
    ],
    illustration: <MatchingIllustration isActive={false} />,
  },
  {
    number: '03',
    title: 'Execute with Confidence',
    description:
      'Work within structured engagements with clear deliverables, escrow payments, and platform-backed quality guarantees.',
    details: [
      'Milestone-based delivery',
      'Escrow-protected payments',
      'Platform arbitration if needed',
    ],
    illustration: <ExecutionIllustration isActive={false} />,
  },
];

function StepCard({
  step,
  index,
  isActive,
  onActivate,
}: {
  step: Step;
  index: number;
  isActive: boolean;
  onActivate: () => void;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => setIsVisible(true), index * 150);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [index]);

  // Render the appropriate illustration based on index
  const renderIllustration = () => {
    if (index === 0) return <ApplicationIllustration isActive={isActive} />;
    if (index === 1) return <MatchingIllustration isActive={isActive} />;
    return <ExecutionIllustration isActive={isActive} />;
  };

  return (
    <div
      ref={ref}
      onClick={onActivate}
      className={cn(
        'relative cursor-pointer transition-all duration-500',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      )}
    >
      <div
        className={cn(
          'relative rounded-2xl transition-all duration-500 overflow-hidden',
          isActive
            ? 'bg-white shadow-2xl shadow-purple-500/10 border-2 border-purple-100'
            : 'bg-white/70 hover:bg-white hover:shadow-xl border-2 border-transparent'
        )}
      >
        {/* Step number watermark */}
        <div
          className={cn(
            'absolute top-4 right-4 text-6xl font-display font-bold transition-colors duration-300',
            isActive ? 'text-purple-100' : 'text-gray-100'
          )}
        >
          {step.number}
        </div>

        {/* Illustration area */}
        <div className="relative pt-6 px-6">
          {renderIllustration()}
        </div>

        {/* Content */}
        <div className="relative p-6 pt-4">
          {/* Title */}
          <h3 className="text-xl font-display font-semibold text-gray-900 mb-2">
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 mb-4 leading-relaxed text-sm">
            {step.description}
          </p>

          {/* Details - expand on active */}
          <div
            className={cn(
              'space-y-2 overflow-hidden transition-all duration-500',
              isActive ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
            )}
          >
            {step.details.map((detail, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-gray-600"
              >
                <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                {detail}
              </div>
            ))}
          </div>
        </div>

        {/* Active indicator bar */}
        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-violet-500 transition-all duration-500',
            isActive ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
    </div>
  );
}

// Progress bar component
function ProgressBar({ activeStep, totalSteps }: { activeStep: number; totalSteps: number }) {
  return (
    <div className="flex items-center justify-center gap-3 mb-12">
      {[...Array(totalSteps)].map((_, index) => (
        <div key={index} className="flex items-center">
          {/* Step dot */}
          <div
            className={cn(
              'w-3 h-3 rounded-full transition-all duration-500',
              index <= activeStep
                ? 'bg-gradient-to-r from-purple-500 to-violet-500 scale-100'
                : 'bg-gray-200 scale-75'
            )}
          />
          {/* Connector line */}
          {index < totalSteps - 1 && (
            <div
              className={cn(
                'w-16 h-0.5 mx-2 transition-all duration-500',
                index < activeStep
                  ? 'bg-gradient-to-r from-purple-500 to-violet-500'
                  : 'bg-gray-200'
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function HowItWorksSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-advance active step
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section
      ref={sectionRef}
      className="relative py-16 lg:py-20 overflow-hidden"
    >
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, hsl(var(--warm-white)) 0%, hsl(270 60% 99%) 50%, hsl(var(--warm-white)) 100%)',
        }}
      />

      {/* Decorative grid pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(hsl(271 81% 56%) 1px, transparent 1px),
              linear-gradient(90deg, hsl(271 81% 56%) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <div
            className={cn(
              'transition-all duration-700',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <p className="text-sm font-semibold text-purple-600 uppercase tracking-wider mb-4">
              How It Works
            </p>
            <h2
              className="font-display font-semibold text-gray-900 mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              From Application to{' '}
              <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                Measurable Results
              </span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              A streamlined journey from verification to outcomes, with
              quality and accountability guaranteed at every step.
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <ProgressBar activeStep={activeStep} totalSteps={steps.length} />

        {/* Steps grid */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {steps.map((step, index) => (
            <StepCard
              key={step.number}
              step={step}
              index={index}
              isActive={activeStep === index}
              onActivate={() => setActiveStep(index)}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={cn(
            'text-center mt-16 transition-all duration-700 delay-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <p className="text-gray-500 mb-4">
            Ready to join a structured ecosystem?
          </p>
          <a
            href="#apply"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 text-white font-semibold shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 transition-all group"
          >
            Start your application
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
